import axios from 'axios';
import { detectFaces, loadModels } from './faceDetectionUtils';  // Added loadModels import here
import { classifyFaceShape } from './faceShapeClassifier';
import { getRecommendationsForShape } from './styleRecommendations';

// Create an axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

/**
 * Upload an image for processing
 * @param {FormData} formData - Form data containing the image file
 * @returns {Promise} Promise resolving to the upload response
 */
export const uploadImage = async (formData) => {
  try {
    // Override the default headers for file upload
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };
    
    const response = await api.post('/upload', formData, config);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Analyze a face in an image
 * @param {FormData} formData - Form data containing the image file
 * @returns {Promise} Promise resolving to the analysis results
 */
export const analyzeFace = async (formData) => {
  try {
    // First upload the image
    const uploadResponse = await uploadImage(formData);
    
    if (!uploadResponse.success) {
      throw new Error(uploadResponse.message || 'Failed to upload image');
    }
    
    // Then analyze the face using the uploaded image path
    const response = await api.post('/analysis', {
      imagePath: uploadResponse.file.path
    });
    
    return response.data;
  } catch (error) {
    console.error('Server analysis failed, falling back to client-side analysis', error);
    // If server-side analysis fails, try client-side analysis
    const file = formData.get('image');
    if (file) {
      return analyzeLocalImage(URL.createObjectURL(file));
    }
    handleApiError(error);
    throw error;
  }
};

/**
 * Client-side analysis for direct image data
 * @param {string} imageUrl - Base64 encoded image data URL
 * @returns {Promise} Promise resolving to analysis results
 */
export const analyzeLocalImage = async (imageUrl) => {
  try {
    console.log('Performing client-side face analysis on image');
    
    // Detect faces using BlazeFace
    const faces = await detectFaces(imageUrl);
    
    if (!faces || faces.length === 0) {
      throw new Error('No faces detected in the image. Please try again with a clearer photo showing your full face.');
    }
    
    console.log(`Detected ${faces.length} face(s) in the image.`);
    
    // Classify face shape using detected face
    const faceShape = classifyFaceShape(faces);
    console.log('Determined face shape:', faceShape);
    
    // Get confidence level (this is a simplified calculation)
    const confidence = calculateConfidence(faces);
    console.log('Confidence level:', confidence);
    
    // Get recommendations for the face shape
    const recommendations = getRecommendationsForShape(faceShape);
    
    // Return analysis results
    return {
      success: true,
      analysis: {
        faceShape,
        confidence,
        description: recommendations.description,
        recommendations: {
          glasses: recommendations.glasses,
          hairStyles: recommendations.hairStyles,
          clothingColors: recommendations.clothingColors
        }
      }
    };
  } catch (error) {
    console.error('Error in local image analysis:', error);
    throw new Error('Failed to analyze image. ' + error.message);
  }
};
/**
 * Calculate confidence level for face shape detection
 * @param {Array} faces - Array of faces detected by BlazeFace
 * @returns {number} Confidence level (0-100)
 */
const calculateConfidence = (faces) => {
  // This is a simplified confidence calculation
  // In a real implementation, this would be more sophisticated
  
  if (!faces || faces.length === 0) return 0;
  
  const face = faces[0];
  
  // Use the detection probability from BlazeFace
  if (face.probability) {
    return Math.round(face.probability[0] * 100);
  }
  
  // If no probability is available, estimate based on detection quality
  const landmarks = face.landmarks;
  const hasGoodLandmarks = landmarks && landmarks.length >= 6;
  
  return hasGoodLandmarks ? 85 : 70;
};

/**
 * Handle API errors
 * @param {Error} error - The error object from axios
 */
const handleApiError = (error) => {
  // Log the error for debugging
  console.error('API Error:', error);
  
  if (error.response) {
    // The server responded with a status code outside the 2xx range
    console.error('Response data:', error.response.data);
    console.error('Response status:', error.response.status);
  } else if (error.request) {
    // The request was made but no response was received
    console.error('No response received:', error.request);
  }
  
  // Return a standardized error message
  const errorMessage = error.response?.data?.message || 
                       error.message || 
                       'An unexpected error occurred';
  
  return {
    success: false,
    message: errorMessage
  };
};

export default {
  uploadImage,
  analyzeFace,
  analyzeLocalImage
};